import ArrSVG from '@public/icons/btn-arr.svg';
import { MotionValue, m as motion, useMotionValue } from 'framer-motion';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import Lottie from '@/shared/components/shared/Lottie';
import { useTypograf } from '@/shared/hooks/use-typograf';
import Link from '../../shared/Link';
import { EcosystemItem } from './ecosystem-data';
import { ItemOffset } from './ecosystem-orbit';
import { useEcosystemItemMetaMotion } from './useEcosystemItemMetaMotion';

type ItemMotion = {
    x: MotionValue<number>;
    y: MotionValue<number>;
    pictogramScale: MotionValue<number>;
    descriptionOpacity: MotionValue<number>;
    scrollYProgress: MotionValue<number>;
};

type Props = {
    item: EcosystemItem;
    itemMotion?: ItemMotion;
    itemOffset?: ItemOffset;
    pictogramRef?: (element: HTMLDivElement | null) => void;
};

function EcosystemCard({ item, itemMotion, itemOffset, pictogramRef }: Props) {
    const tpTitle = useTypograf(item.title);
    const tpDescription = useTypograf(item.description);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const [titleHeight, setTitleHeight] = useState(0);
    const pictogramScaleFallback = useMotionValue(1);
    const descriptionOpacityFallback = useMotionValue(1);
    const pictogramScale = itemMotion?.pictogramScale ?? pictogramScaleFallback;
    const descriptionOpacity = itemMotion?.descriptionOpacity ?? descriptionOpacityFallback;
    const scrollYProgressFallback = useMotionValue(1);
    const scrollYProgress = itemMotion?.scrollYProgress ?? scrollYProgressFallback;
    const metaMotion = useEcosystemItemMetaMotion(pictogramScale, titleHeight, descriptionOpacity, scrollYProgress);

    useLayoutEffect(() => {
        const titleEl = titleRef.current;

        if (!itemMotion || !titleEl) {
            setTitleHeight(0);

            return;
        }

        const updateHeight = () => setTitleHeight(titleEl.offsetHeight);

        updateHeight();

        const observer = new ResizeObserver(updateHeight);

        observer.observe(titleEl);

        return () => observer.disconnect();
    }, [itemMotion, tpTitle]);

    const { href, lottie } = item;

    const wrapAnchor = (content: ReactNode) =>
        href ? (
            <Link href={href} className="ecosystem-section__item-anchor">
                {content}
            </Link>
        ) : (
            content
        );

    if (!itemMotion) {
        const itemStyle =
            itemOffset && (itemOffset.x !== 0 || itemOffset.y !== 0)
                ? { transform: `translate3d(${itemOffset.x}px, ${itemOffset.y}px, 0)` }
                : undefined;

        return (
            <li className="ecosystem-section__item" style={itemStyle}>
                <div className="ecosystem-section__item-link">
                    {wrapAnchor(
                        <>
                            <div ref={pictogramRef} className="ecosystem-section__pictogram">
                                <Lottie src={lottie.src} className="ecosystem-section__pictogram-img img-fluid" />
                            </div>
                            <div className="ecosystem-section__item-title h3">
                                <span className="ecosystem-section__item-title-text">{tpTitle}</span>
                                {href && (
                                    <span className="ecosystem-section__item-title-icon" aria-hidden="true">
                                        <ArrSVG />
                                    </span>
                                )}
                            </div>
                        </>,
                    )}
                    <div
                        className="ecosystem-section__item-description text-m wysiwyg"
                        dangerouslySetInnerHTML={{ __html: tpDescription }}
                    ></div>
                </div>
            </li>
        );
    }

    return (
        <motion.li className="ecosystem-section__item" style={{ x: itemMotion.x, y: itemMotion.y }}>
            <div className="ecosystem-section__item-link">
                {wrapAnchor(
                    <>
                        <motion.div
                            ref={pictogramRef}
                            className="ecosystem-section__pictogram"
                            style={{
                                scale: itemMotion.pictogramScale,
                            }}
                        >
                            <Lottie src={lottie.src} className="ecosystem-section__pictogram-img img-fluid" />
                        </motion.div>
                        <motion.div
                            ref={titleRef}
                            className="ecosystem-section__item-title h3"
                            style={{
                                scale: metaMotion.titleScale,
                                y: metaMotion.titleY,
                            }}
                        >
                            <span className="ecosystem-section__item-title-text">{tpTitle}</span>
                            {href && (
                                <motion.span
                                    className="ecosystem-section__item-title-icon"
                                    aria-hidden="true"
                                    style={{ opacity: metaMotion.iconOpacity }}
                                >
                                    <ArrSVG />
                                </motion.span>
                            )}
                        </motion.div>
                    </>,
                )}
                <motion.div
                    className="ecosystem-section__item-description text-m wysiwyg"
                    style={{
                        y: metaMotion.descriptionY,
                        opacity: metaMotion.descriptionOpacity,
                    }}
                    dangerouslySetInnerHTML={{ __html: tpDescription }}
                ></motion.div>
            </div>
        </motion.li>
    );
}

export default EcosystemCard;
